export function toFiniteNumber(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

const formatNumber = (value, decimals = 0) => {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  const absolute = Math.abs(number);
  const [integer, fraction] = absolute.toFixed(decimals).split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const sign = number < 0 ? "-" : "";

  return `${sign}${grouped}${fraction !== undefined ? `.${fraction}` : ""}`;
};

const formatCompactCurrency = (value, currency) => {
  const number = toFiniteNumber(value);
  if (number === null) return "—";

  const absolute = Math.abs(number);
  const sign = number < 0 ? "-" : "";

  if (absolute >= 1000000) {
    const millions = Number.parseFloat((absolute / 1000000).toFixed(2));
    return `${currency} ${sign}${millions}m`;
  }
  if (absolute >= 1000) return `${currency} ${sign}${Math.round(absolute / 1000)}k`;

  return `${currency} ${sign}${Math.round(absolute)}`;
};

export function getKpiUnitLabel(unit, currency = "CHF") {
  if (unit === "percent") return "%";
  if (unit === "hours") return "h";
  if (unit === "currencyPerHour") return `${currency}/h`;
  return currency;
}

const badgeDecimals = (unit, decimals) => decimals ?? (unit === "percent" ? 1 : unit === "currencyPerHour" ? 2 : 0);
const badgeNumber = (number, unit) => (unit === "percent" ? number * 100 : number);

export function formatKpiBadgeValue(value, unit, decimals) {
  const number = toFiniteNumber(value);
  if (number === null) return "—";
  return formatNumber(unit === "hours" ? Math.ceil(number) : badgeNumber(number, unit), badgeDecimals(unit, decimals)).replace(/\.0+$/, "");
}

export function formatKpiBadgeDifference(value, unit, decimals) {
  const number = toFiniteNumber(value);
  if (number === null) return "—";
  const sign = isKpiDifferenceRoundedToZero(number, unit, decimals) ? "" : number > 0 ? "+" : "-";
  return `${sign}${formatNumber(Math.abs(badgeNumber(number, unit)), badgeDecimals(unit, decimals)).replace(/\.0+$/, "")}`;
}

export function isKpiDifferenceRoundedToZero(value, unit, decimals) {
  const number = toFiniteNumber(value);
  if (number === null) return true;
  return Number(Math.abs(badgeNumber(number, unit)).toFixed(badgeDecimals(unit, decimals))) === 0;
}

export function formatDirectToIndirect(value) {
  const share = toFiniteNumber(value);
  if (share === null || share <= 0 || share > 1) return "—";
  return `${formatNumber((1 - share) / share, 1)}:1`;
}

export function formatKpiScaleValue(value, unit, currency = "CHF") {
  const number = toFiniteNumber(value);
  if (number === null) return "—";
  if (unit === "percent") return `${formatNumber(number * 100, 1)}%`;
  if (unit === "currencyPerHour") return `${currency} ${Math.round(number)}/h`;
  if (unit === "hours") return `${formatNumber(number, 0)}h`;
  return formatCompactCurrency(number, currency);
}
